/**
 * AI チャットパネルの開閉と、続きの会話 ID を持つ。
 *
 * パネルは開くたびにホーム（用途のボタン）から始まる。最後の会話 ID はユーザーごとに
 * localStorage に覚えておき、ホームの「前の会話の続き」から開けるようにする。
 * 履歴そのものはサーバーにあり（30 日で消える）、ここには ID しか置かない。
 * 文脈のイベント（「このイベント」）は、イベント画面から開いたときだけ持ち、
 * ヘッダーから開いたときは持たない（前に開いた画面のイベントが残らないように）。
 *
 * 利用規約・プライバシーポリシーの今の版への同意と表示名の登録が済むまでは、値を null にする。
 * 入口（ヘッダー・ダッシュボード・イベント画面）とパネルはどれも useChat() が null なら出ないので、
 * 同意画面や案内ページ（同意の確認から外れている）からも開けなくなる。API も同意を確かめる。
 * 「確認」でよい改定を確認していないあいだは、入口は出したまま、押すと改定の告知を開く
 * （contexts/PolicyNoticeContext.tsx。パネルは開かない）。
 */

import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { usePolicyNotice } from '@/hooks/usePolicyNotice';
import { useUser } from '@/hooks/useUser';
import { CHAT_ENTRY_POINT, chatConversationStorageKey } from '@/constants/chat';
import { ChatContext, type OpenChatOptions } from '@/contexts/chatContextObj';
import type { ChatEntryPoint } from '@/services/chat';

function readStoredConversation(userId: string | undefined): string | null {
  if (!userId) return null;
  try {
    return window.localStorage.getItem(chatConversationStorageKey(userId));
  } catch {
    return null;
  }
}

function writeStoredConversation(userId: string | undefined, conversationId: string | null) {
  if (!userId) return;
  try {
    const key = chatConversationStorageKey(userId);
    if (conversationId) {
      window.localStorage.setItem(key, conversationId);
    } else {
      window.localStorage.removeItem(key);
    }
  } catch {
    // 保存できなくても会話はできる（再読み込みで新しい会話になるだけ）
  }
}

export function ChatProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { isProfileComplete, policiesCurrent } = useUser();
  const { openNotice } = usePolicyNotice();
  const userId = user?.id;
  const [isOpen, setIsOpen] = useState(false);
  const [openCount, setOpenCount] = useState(0);
  const [contextEventId, setContextEventId] = useState<string | null>(null);
  const [entry, setEntry] = useState<NonNullable<ChatEntryPoint>>(CHAT_ENTRY_POINT.OTHER);
  // ユーザーが切り替わったら、そのユーザーの保存値を読み直す
  const [conversation, setConversation] = useState<{
    userId: string | undefined;
    id: string | null;
  }>(() => ({ userId, id: readStoredConversation(userId) }));
  const conversationId =
    conversation.userId === userId ? conversation.id : readStoredConversation(userId);

  const setConversationId = useCallback(
    (id: string | null) => {
      setConversation({ userId, id });
      writeStoredConversation(userId, id);
    },
    [userId],
  );

  const openChat = useCallback((options?: OpenChatOptions) => {
    setContextEventId(options?.eventId ?? null);
    setEntry(
      options?.entry ?? (options?.eventId ? CHAT_ENTRY_POINT.EVENT_PAGE : CHAT_ENTRY_POINT.HEADER),
    );
    setOpenCount((count) => count + 1);
    setIsOpen(true);
  }, []);

  const closeChat = useCallback(() => setIsOpen(false), []);
  const clearContext = useCallback(() => setContextEventId(null), []);

  const value = useMemo(
    () => ({
      isOpen,
      openCount,
      contextEventId,
      entry,
      conversationId,
      openChat,
      closeChat,
      setConversationId,
      clearContext,
    }),
    [isOpen, openCount, contextEventId, entry, conversationId, openChat, closeChat, setConversationId, clearContext],
  );

  // 改定を確認するまでは、入口から改定の告知を開く
  const noticeValue = useMemo(
    () => ({ ...value, isOpen: false, openChat: () => openNotice() }),
    [value, openNotice],
  );

  const provided = !isProfileComplete ? null : policiesCurrent ? value : noticeValue;
  return <ChatContext.Provider value={provided}>{children}</ChatContext.Provider>;
}
